import { useEffect, useRef, useState } from "react";

/**
 * "Small Animal, Big Nutrition — The Perfect Diet" infographic.
 *
 * Base image (928×1152, 4:5) has four baked-in horizontal leader lines at:
 *   Line 1 — y ≈ 59%  →  70% TIMOTHY HAY (Always Available)
 *   Line 2 — y ≈ 70%  →  20% FORTIFIED FOOD
 *   Line 3 — y ≈ 78%  →   8% GREENS
 *   Line 4 — y ≈ 90%  →   2% TREATS (left dot at x≈23%)
 *
 * The vertical gap between consecutive lines is only 8–12% of container
 * height. Because the container shrinks on mobile, each text block occupies
 * a larger % of that limited vertical budget. To guarantee zero overlap
 * (block-on-line AND block-on-block) across every viewport, mobile uses
 * compact type sizes and single-line captions. Desktop (md:) keeps the
 * original large serif hierarchy.
 *
 * Positioning convention: each label is anchored with `bottom:` so its
 * baseline sits ~1–2% above its corresponding line — the line appears to
 * emerge from just beneath the caption and point directly at the label.
 */
export default function NutritionInfographic() {
  const [start, setStart] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setStart(true), 220);
    return () => clearTimeout(t);
  }, []);

  const labelCls = (delay) => ({
    opacity: start ? 1 : 0,
    transform: start ? "translateY(0)" : "translateY(12px)",
    transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`,
  });

  return (
    <div
      ref={ref}
      data-testid="nutrition-infographic"
      className="relative aspect-[4/5] select-none"
    >
      {/* Rabbit silhouette with baked-in leader lines */}
      <img
        src="/hopsy-diet-notext-0.webp"
        alt=""
        className="absolute inset-0 w-full h-full object-contain"
        style={{
          opacity: start ? 1 : 0,
          transform: start ? "scale(1)" : "scale(0.985)",
          transition: "opacity 900ms ease-out, transform 1200ms ease-out",
        }}
      />

      {/* Title — top-left */}
      <div className="absolute top-[2%] left-[2%] right-[38%]" style={labelCls(100)}>
        <p className="font-serif text-xl md:text-3xl lg:text-4xl text-forest leading-[1.1]">
          Small Animal, Big Nutrition<span className="text-terra">:</span>
        </p>
        <p className="font-serif italic text-xl md:text-2xl lg:text-3xl text-terra leading-[1.1] mt-1">
          The Perfect Diet
        </p>
      </div>

      {/* UNLIMITED FRESH WATER — sits just ABOVE the baked-in water droplet.
          Droplet starts at y≈22% in the source PNG, so the label is anchored
          at top-[13%] (text extends to ~y=17%) leaving a clean ~5% gap
          before the drop. Close enough to read as "attached", but with zero
          overlap between text and drop glyph. */}
      <div
        className="absolute top-[9%] md:top-[13%] right-[1%] text-right max-w-[30%] md:max-w-[24%]"
        style={labelCls(1500)}
      >
        <p className="font-sans font-semibold text-[8px] md:text-[11px] tracking-[0.2em] md:tracking-[0.22em] uppercase text-forest leading-[1.25]">
          Unlimited<br />Fresh Water
        </p>
      </div>

      {/* 70% TIMOTHY HAY — Line 1 at y=59%; block bottom anchored at y≈57% */}
      <div
        className="absolute left-[2%] max-w-[46%]"
        style={{ ...labelCls(650), bottom: "43%" }}
      >
        <p className="font-serif text-xl md:text-4xl lg:text-5xl font-semibold text-forest leading-none">70<span className="text-terra">%</span></p>
        <p className="font-sans font-semibold text-[10px] md:text-xs tracking-[0.12em] uppercase text-forest mt-1 leading-none">Timothy Hay</p>
        <p className="font-sans text-[9px] md:text-[10px] tracking-[0.22em] uppercase text-ink-muted mt-1 leading-none">Always Available</p>
      </div>

      {/* 20% FORTIFIED FOOD — Line 2 at y=70%; block bottom at y≈69%.
          Single-line caption on both mobile and desktop so the whole block
          fits in the 11% gap between Line 1 and Line 2. */}
      <div
        className="absolute left-[2%] max-w-[44%]"
        style={{ ...labelCls(950), bottom: "31%" }}
      >
        <p className="font-serif text-base md:text-3xl lg:text-4xl font-semibold text-forest leading-none">20<span className="text-terra">%</span></p>
        <p className="font-sans font-semibold text-[9px] md:text-xs tracking-[0.12em] uppercase text-forest mt-1 leading-none">Fortified Food</p>
      </div>

      {/* 8% GREENS — Line 3 at y=78%; block bottom at y≈77%.
          Block must fit in the very tight 8% gap between Line 2 and Line 3.
          Kept compact on all breakpoints so the block never crosses Line 2. */}
      <div
        className="absolute left-[2%] max-w-[30%]"
        style={{ ...labelCls(1200), bottom: "23%" }}
      >
        <p className="font-serif text-sm md:text-xl lg:text-2xl font-semibold text-forest leading-none">8<span className="text-terra">%</span></p>
        <p className="font-sans font-semibold text-[9px] md:text-[10px] tracking-[0.12em] uppercase text-forest mt-0.5 leading-none">Greens</p>
      </div>

      {/* 2% TREATS — Line 4 at y=90% (left dot at x≈23%); block bottom at y≈89%.
          max-w capped so the label stays inside x<22% and never overlaps Line 4. */}
      <div
        className="absolute left-[2%] max-w-[20%]"
        style={{ ...labelCls(1400), bottom: "11%" }}
      >
        <p className="font-serif text-base md:text-3xl lg:text-4xl font-semibold text-forest leading-none">2<span className="text-terra">%</span></p>
        <p className="font-sans font-semibold text-[9px] md:text-xs tracking-[0.12em] uppercase text-forest mt-1 leading-none">Treats</p>
      </div>
    </div>
  );
}
